import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { logout } from '@/lib/logout';
import { useUser } from '@/providers/UserProvider';
import { API } from '@/utils/API';

interface SessionStatus {
  authenticated: boolean;
  expires_at?: string | null;
  timeout_seconds?: number;
  warning_seconds?: number;
}

const minutesToSeconds = (minutes: number) => minutes * 60;

const FALLBACK_WARNING_SECONDS = minutesToSeconds(15);
const STATUS_POLL_INTERVAL_MS = 60 * 1000;

const formatClock = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatRemainingText = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const minuteLabel = minutes === 1 ? 'minute' : 'minutes';
  const secondLabel = seconds === 1 ? 'second' : 'seconds';

  if (minutes === 0) return `${seconds} ${secondLabel} remaining`;

  return `${minutes} ${minuteLabel} and ${seconds} ${secondLabel} remaining`;
};

const secondsUntil = (isoTimestamp?: string | null) => {
  if (!isoTimestamp) return null;

  return Math.max(0, Math.ceil((Date.parse(isoTimestamp) - Date.now()) / 1000));
};

const SessionTimeoutWarning = () => {
  const { userData } = useUser();
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [warningSeconds, setWarningSeconds] = useState(
    FALLBACK_WARNING_SECONDS
  );
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [isExtending, setIsExtending] = useState(false);
  const hasLoggedOutRef = useRef(false);

  const startLogoutIfNeeded = useCallback(() => {
    if (hasLoggedOutRef.current) return;

    hasLoggedOutRef.current = true;
    void logout();
  }, []);

  const applySessionStatus = useCallback(
    (data: SessionStatus) => {
      if (!data.authenticated) {
        startLogoutIfNeeded();
        return false;
      }

      const nextExpiresAt = data.expires_at ?? null;
      setExpiresAt(nextExpiresAt);
      setRemainingSeconds(secondsUntil(nextExpiresAt));
      setWarningSeconds(data.warning_seconds ?? FALLBACK_WARNING_SECONDS);

      return true;
    },
    [startLogoutIfNeeded]
  );

  const loadSessionStatus = useCallback(async () => {
    if (!userData) return;

    const response = await API.fetch('/session_status');
    const data = (await response.json()) as SessionStatus;

    applySessionStatus(data);
  }, [applySessionStatus, userData]);

  useEffect(() => {
    if (!userData) {
      setExpiresAt(null);
      setRemainingSeconds(null);
      hasLoggedOutRef.current = false;
      return;
    }

    void loadSessionStatus();
    const pollId = window.setInterval(
      () => void loadSessionStatus(),
      STATUS_POLL_INTERVAL_MS
    );

    return () => window.clearInterval(pollId);
  }, [loadSessionStatus, userData]);

  useEffect(() => {
    if (!expiresAt) {
      setRemainingSeconds(null);
      return;
    }

    const tick = () => {
      const nextRemainingSeconds = secondsUntil(expiresAt);
      setRemainingSeconds(nextRemainingSeconds);

      if (nextRemainingSeconds === 0 && !hasLoggedOutRef.current) {
        hasLoggedOutRef.current = true;
        void logout();
      }
    };

    tick();
    const tickId = window.setInterval(tick, 1000);

    return () => window.clearInterval(tickId);
  }, [expiresAt]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    setRemainingSeconds(null);

    try {
      const response = await API.authenticated.fetch('/session_status', {
        method: 'PATCH',
      });
      const data = (await response.json()) as SessionStatus;

      if (!response.ok) {
        startLogoutIfNeeded();
        return;
      }

      applySessionStatus(data);
    } catch {
      startLogoutIfNeeded();
    } finally {
      setIsExtending(false);
    }
  };

  if (!userData || remainingSeconds === null) return null;

  const shouldShowWarning =
    remainingSeconds > 0 && remainingSeconds <= warningSeconds;

  return (
    <Dialog open={shouldShowWarning}>
      <DialogContent showCloseButton={false} className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Session Timeout Warning
          </DialogTitle>
          <DialogDescription className="text-lg">
            Your session will expire due to inactivity. Would you like to extend
            your session?
          </DialogDescription>
        </DialogHeader>

        <div className="py-8 text-center">
          <div className="text-6xl font-bold text-destructive">
            {formatClock(remainingSeconds)}
          </div>
          <p className="mt-4 text-lg text-muted-foreground">
            {formatRemainingText(remainingSeconds)}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" size="lg" onClick={() => void logout()}>
            Logout Now
          </Button>
          <Button
            size="lg"
            onClick={() => void handleExtendSession()}
            disabled={isExtending}
          >
            {isExtending ? 'Extending...' : 'Extend Session'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export {
  SessionTimeoutWarning,
  formatClock,
  formatRemainingText,
  minutesToSeconds,
  secondsUntil,
};
